import React, { useState, useEffect } from 'react';
import { numbersWithSlash } from '../utils/inputValidation';
import styles from '../css-isolated/hr_physical_examination.module.css';

// Suppress ResizeObserver errors
const resizeObserverErrorHandler = (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
    e.stopImmediatePropagation();
    return false;
  }
};

window.addEventListener('error', resizeObserverErrorHandler);

function PhysicalExamination({ values = {}, onChange, isDarkMode = false, activeSection: externalActiveSection, setActiveSection: externalSetActiveSection }) {
  // Active section state (1 = Vital Signs + Detailed Findings, 2 = Physical Examination Checklist)
  // Use external state if provided (from HRWizard), otherwise use internal state
  const [internalActiveSection] = useState(1);
  const activeSection = externalActiveSection !== undefined ? externalActiveSection : internalActiveSection;
  const defaultVitals = {
    temperature: '',
    bp: '',
    hr: '',
    rr: '',
    height: '',
    weight: '',
    bmi: ''
  };
  const defaultFindings = [
    { label: 'General appearance', note: 'upright with coordinated movement, no signs of distress', value: null },
    { label: 'Head, eyes, ears, nose and throat', note: 'Normocephalic, pink palpebral conjunctiva, white and clear sclera, PERLA, no nasal/aural discharges, no tonsillo-pharyngeal congestion', value: null },
    { label: 'Neck', note: 'no lymph nodes palpable', value: null },
    { label: 'Chest/lungs', note: 'symmetrical chest and lung expansion, clear both sounds', value: null },
    { label: 'Heart', note: 'regular rhythm, no abnormal heart sounds', value: null },
    { label: 'Abdomen', note: 'flat, soft, no masses, normal bowel sounds', value: null },
    { label: 'Genito-urinary', note: 'no lesions, no urethral discharges, no palpable hernia', value: null },
    { label: 'Musculo-skeletal', note: 'no gross deformities, no limitation of movement', value: null },
    { label: 'Extremities', note: 'no swelling/deformity, no weakness, good capillary refill, strong pulses, normal flexes', value: null },
    { label: 'Other significant findings (specify)', note: '', value: null }
  ];
  const vitals = { ...defaultVitals, ...(values.vitals || {}) };

  // For backward compatibility, ensure findings is always an array of length 10
  let findings = values.findings || defaultFindings;
  if (findings.length < 10) {
    findings = [...defaultFindings];
  }
  const detailedFindings = values.detailedFindings || '';

  const handleVitalsChange = (e) => {
    const { name, value } = e.target;
    
    // Validate blood pressure format (number/number)
    if (name === 'bp') {
      // Allow empty value or valid format: digits/digits
      // Pattern allows: 120/80, 110/70, etc.
      const bpPattern = /^\d*\/?\d*$/;
      if (value !== '' && !bpPattern.test(value)) {
        return; // Reject invalid input
      }
    }
    
    const updated = { ...vitals, [name]: value };
    onChange && onChange({ ...values, vitals: updated });
  };

  // Auto-calculate BMI when height or weight changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const height = parseFloat(vitals.height);
      const weight = parseFloat(vitals.weight);
      
      // Only calculate if both height and weight are valid numbers
      if (height && weight && height > 0) {
        // BMI formula: weight (kg) / height² (m²)
        const calculatedBMI = weight / (height * height);
        const roundedBMI = calculatedBMI.toFixed(2);
        
        // Only update if the calculated BMI is different from current BMI
        if (vitals.bmi !== roundedBMI) {
          const updated = { ...vitals, bmi: roundedBMI };
          onChange && onChange({ ...values, vitals: updated });
        }
      } else if (vitals.bmi && (!height || !weight)) {
        // Clear BMI if height or weight is cleared
        const updated = { ...vitals, bmi: '' };
        onChange && onChange({ ...values, vitals: updated });
      }
    }, 100); // Debounce the calculation

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vitals.height, vitals.weight]); // Only run when height or weight changes


  const handleFindingChange = (idx, val) => {
    const updated = findings.map((f, i) => i === idx ? { ...f, value: val } : f);
    onChange && onChange({ ...values, findings: updated });
  };

  const handleOtherFindingChange = (e) => {
    const updated = findings.map((f, i) => i === 9 ? { ...f, value: e.target.value } : f);
    onChange && onChange({ ...values, findings: updated });
  };

  const handleDetailedFindingsChange = (e) => {
    onChange && onChange({ ...values, detailedFindings: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Let wizard handle submit
  };

  // Tab navigation removed for wizard compatibility

  return (
    <div className={`${styles['hr-wizard-container']} ${isDarkMode ? styles['dark-mode'] : styles['light-mode']}`}>
        <form onSubmit={handleSubmit} autoComplete="off" className={styles.contentWrapper}>
            {/* Section 1: Vital Signs (left) and Detailed Findings (right) */}
            {activeSection === 1 && (
            <>
            <div className={styles.formSection} style={{ gridColumn: '1 / 2', gridRow: '1 / 2' }}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h2 className={styles.sectionTitle}>Vital Signs</h2>
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Temperature (°C)</label>
                    <input 
                      type="number" 
                      name="temperature" 
                      value={vitals.temperature} 
                      onChange={handleVitalsChange} 
                      className={styles.formInput} 
                      step="any" 
                      placeholder="36.5"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Blood Pressure (mmHg)</label>
                    <input 
                      type="text" 
                      name="bp" 
                      value={vitals.bp} 
                      onChange={handleVitalsChange} 
                      onKeyDown={numbersWithSlash}
                      className={styles.formInput} 
                      placeholder="120/80"
                      pattern="\d+/\d+"
                      title="Please enter blood pressure in format: number/number (e.g., 120/80)"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Heart Rate (bpm)</label>
                    <input 
                      type="number" 
                      name="hr" 
                      value={vitals.hr} 
                      onChange={handleVitalsChange} 
                      className={styles.formInput} 
                      step="any" 
                      placeholder="72"
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Respiratory Rate (cpm)</label>
                    <input 
                      type="number" 
                      name="rr" 
                      value={vitals.rr} 
                      onChange={handleVitalsChange} 
                      className={styles.formInput} 
                      step="any" 
                      placeholder="16"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Height (m)</label>
                    <input 
                      type="number" 
                      name="height" 
                      value={vitals.height} 
                      onChange={handleVitalsChange} 
                      className={styles.formInput} 
                      step="any" 
                      placeholder="1.70"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Weight (kg)</label>
                    <input 
                      type="number" 
                      name="weight" 
                      value={vitals.weight} 
                      onChange={handleVitalsChange} 
                      className={styles.formInput} 
                      step="any" 
                      placeholder="70"
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>BMI (kg/m²) <span style={{fontSize: '12px', color: '#7091E6'}}>(Auto-calculated)</span></label>
                    <input 
                      type="text" 
                      name="bmi" 
                      value={vitals.bmi} 
                      readOnly
                      className={styles.formInput} 
                      placeholder="Auto-calculated from height and weight"
                      style={{backgroundColor: '#f0f4f8', cursor: 'not-allowed'}}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    {/* Empty space for layout balance */}
                  </div>
                  <div className={styles.formGroup}>
                    {/* Empty space for layout balance */}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.formSection} style={{ gridColumn: '2 / 3', gridRow: '1 / 2' }}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h2 className={styles.sectionTitle}>Detailed Findings</h2>
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={`${styles.formLabel} ${styles.optionalLabel}`}>Additional Details</label>
                  <textarea
                    className={styles.formTextarea}
                    value={detailedFindings}
                    onChange={handleDetailedFindingsChange}
                    placeholder="Enter detailed findings, observations, or additional notes here..."
                  />
                </div>
              </div>
            </div>
            </>
            )}

            {/* Section 2: Physical Examination Checklist */}
            {activeSection === 2 && (
            <div className={styles.formSection} style={{ gridColumn: '1 / 3', gridRow: '1 / 2', width: '100%' }}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h2 className={styles.sectionTitle}>Physical Examination Checklist</h2>
              </div>
              <div className={styles.physicalChecklist}>
                <div className={`${styles.checklistRow} ${styles.checklistHeader}`}>
                  <div className={styles.checklistLabel}></div>
                  <div className={`${styles.checklistRadio} ${styles.checklistRadioHeader}`}>Yes</div>
                  <div className={`${styles.checklistRadio} ${styles.checklistRadioHeader}`}>No</div>
                </div>
                {findings.map((item, idx) => (
                  idx === 9 ? (
                    <div className={`${styles.checklistRow} ${styles.checklistOtherRow}`} key={item.label}>
                      <div className={`${styles.checklistLabel} ${styles.checklistOtherLabelFull}`}>
                        <span className={styles.checklistTitle}>{idx + 1}. {item.label}</span>
                        {item.note && <span className={styles.checklistNote}> ({item.note})</span>}
                        <input
                          className={styles.checklistOtherInput}
                          type="text"
                          name={`finding${idx}`}
                          value={item.value || ''}
                          onChange={handleOtherFindingChange}
                          placeholder="Specify other significant findings"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className={styles.checklistRow} key={item.label}>
                      <div className={styles.checklistLabel}>
                        <span className={`${styles.checklistTitle} ${styles.requiredChecklistTitle}`}>{idx + 1}. {item.label}</span>
                        {item.note && <span className={styles.checklistNote}> ({item.note})</span>}
                      </div>
                      <div className={styles.checklistRadio}>
                        <input 
                          type="radio" 
                          name={`finding${idx}`} 
                          checked={item.value === true} 
                          onChange={() => handleFindingChange(idx, true)}
                          className={styles.radioInput}
                        />
                      </div>
                      <div className={styles.checklistRadio}>
                        <input 
                          type="radio" 
                          name={`finding${idx}`} 
                          checked={item.value === false} 
                          onChange={() => handleFindingChange(idx, false)}
                          className={styles.radioInput}
                        />
                      </div>
                    </div>
                  )
                ))}
              </div>
            </div>
            )}

        </form>
    </div>
  );
}

export default PhysicalExamination; 